import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { AppState } from 'store/store';
import { NoticeStatus } from 'store/ducks/notice/types';
import { timeslotsServices } from 'api/services';
import { isSuRole } from 'helpers/checkRoles';
import { ShowNotification } from 'helpers/showNotice';
import { MainButton } from 'components/MainButton/MainButton';
import Template from 'components/Template/Template';
import Confirmation from 'components/Confirmation/Confirmation';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { IResTimeslot, ITimeslotParams } from 'api/services/timeslots/types';
import { getBranches } from 'store/ducks/services/actions';

import './TimeslotsPage.sass';

export const TimeslotsPage: React.FC = () => {
  const [timeslots, setTimeslots] = useState<IResTimeslot[]>([]);
  const [selectedTimeslot, setSelectedTimeslot] = useState<number>();
  const { user } = useSelector((state: AppState) => state.users);
  const confirmationRef = useRef<any>();
  const [timeslotDate, setTimeslotDate] = useState(
    moment().format('YYYY-MM-DD')
  );
  const [branchId, setBranchId] = useState<number | null>(null);
  const [hasError, setHasError] = useState(false);
  const i18n = useSelector(
    (state: AppState) => state.i18n.data.pages.timeslotsPage
  );
  const lang = useSelector((state: AppState) => state.i18n.key);
  const { branches } = useSelector((state: AppState) => state.services);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getBranches());
  }, []);

  useEffect(() => {
    if (branches.length && !branchId) {
      setBranchId(3);
      getTimeslots(3);
    }
  }, [branches.length]);

  const getTimeslots = async (initBranch?: number) => {
    if ((!branchId || !timeslotDate.trim()) && !initBranch) {
      ShowNotification(i18n.selectDateAndBranch, NoticeStatus.ERROR);
      setHasError(true);
      return;
    }
    const selectedBranch = initBranch || branchId;
    if (!selectedBranch) return;

    const params: ITimeslotParams = {
      date: timeslotDate,
      branchId: selectedBranch,
    };
    const res = await timeslotsServices
      .getTimeslots(params)
      .then((res) => res.data);
    setTimeslots(res);
  };

  const deleteTimeslot = async () => {
    if (!selectedTimeslot) return;
    await timeslotsServices.deleteTimeslot(selectedTimeslot);
    ShowNotification(i18n.timeslotDeleted);
    getTimeslots();
  };

  const searchTimeslots = (e: React.FormEvent) => {
    e.preventDefault();
    getTimeslots();
  };

  const openConfirm = (id: number) => {
    setSelectedTimeslot(id);
    confirmationRef.current.onShow();
  };

  return (
    <Template>
      <div className='timeslots-page'>
        <Confirmation
          submit={deleteTimeslot}
          ref={confirmationRef}
          title={i18n.confirmDeleteTimeslot}
        />
        <div className='timeslots-page__row'>
          <Typography variant='h3'>{i18n.timeslots}</Typography>
          {isSuRole(user) && <MainButton link='/timeslots/create' />}
        </div>
        <form className='users-page__form' onSubmit={searchTimeslots}>
          <Grid className='row' container spacing={2} alignItems='flex-end'>
            <Grid item md={3} xs={12}>
              <TextField
                value={timeslotDate}
                onChange={(e) => setTimeslotDate(e.target.value)}
                label={i18n.createdAt}
                autoComplete='off'
                variant='outlined'
                size='small'
                InputLabelProps={{ shrink: true }}
                type='date'
                error={hasError && !timeslotDate.trim()}
              />
            </Grid>

            <Grid item md={3} xs={12}>
              <FormControl
                fullWidth
                className='select-small'
                error={hasError && !branchId}
              >
                <InputLabel variant='outlined' id='role'>
                  {i18n.branch}
                </InputLabel>
                <Select
                  labelId='branch'
                  label={i18n.branch}
                  onChange={(e) => setBranchId(e.target.value as number)}
                  value={branchId || ''}
                  size='small'
                >
                  {branches.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {i?.[`name${lang}`]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={2} xs={2}>
              <Button
                variant='contained'
                color='success'
                type='submit'
                className='users-page__button'
                size='large'
              >
                {i18n.search}
              </Button>
            </Grid>
          </Grid>
        </form>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>{i18n.fromTime}</TableCell>
                <TableCell>{i18n.toTime}</TableCell>
                <TableCell>{i18n.reservedAndCapacity}</TableCell>
                <TableCell>{i18n.updatedDate}</TableCell>
                {isSuRole(user) && <TableCell>{i18n.actions}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeslots.map((i, index) => (
                <TableRow key={i.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{i.fromTime}</TableCell>
                  <TableCell>{i.toTime}</TableCell>
                  <TableCell>{`${i.reserved}/${i.theoryCapacity}`}</TableCell>
                  <TableCell>
                    {moment(i.updatedAt).format('DD.MM.YYYY')}
                  </TableCell>
                  {isSuRole(user) && (
                    <TableCell>
                      <Grid alignItems='center'>
                        <div className='btn-row'>
                          <Link
                            to={`/timeslots/${i.id}`}
                            style={{ display: 'inline-block' }}
                          >
                            <EditIcon
                              className='edit-icon'
                              sx={{ fontSize: 25, color: '#003956' }}
                            />
                          </Link>
                          <DeleteForeverIcon
                            className='delete-icon'
                            onClick={() => openConfirm(i.id)}
                            sx={{ fontSize: 25, color: '#ff0000' }}
                          />
                        </div>
                      </Grid>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>
    </Template>
  );
};
